import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import useOrganizationTimezone from '../../../hooks/useOrganizationTimezone';
import PatientService from '../../../services/patientService';
import PaymentFormService from '../../../services/paymentFormService';
import PaymentService from '../../../services/paymentService';
import UserService from '../../../services/userService';
import Button from '../../common/Button';
import PatientAutocomplete from '../../common/PatientAutocomplete';
import './PaymentCreateForm.css';

const getDateInTimezone = (timezone) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};

const PaymentCreateForm = ({ isModal = false, onSuccess, onCancel, defaultPatientId }) => {
  const auth = useAuth();
  const { timezone } = useOrganizationTimezone();
  const [dateTouched, setDateTouched] = useState(false);
  const [formData, setFormData] = useState({
    patientId: defaultPatientId || '',
    paymentFormId: '',
    amount: '',
    date: getDateInTimezone('America/Mexico_City'),
    concept: '',
    note: '',
    allocationUserId: 'GENERAL_SALES'
  });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [paymentForms, setPaymentForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingForms, setIsLoadingForms] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (defaultPatientId && !formData.patientId) {
      setFormData(prev => ({ ...prev, patientId: defaultPatientId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPatientId]);

  useEffect(() => {
    if (!dateTouched && timezone) {
      setFormData(prev => ({ ...prev, date: getDateInTimezone(timezone) }));
    }
  }, [timezone, dateTouched]);

  useEffect(() => {
    const loadForms = async () => {
      try {
        setIsLoadingForms(true);
        const result = await PaymentFormService.getPaymentForms(auth.user?.access_token);
        const list = result.paymentForms || result.data || [];
        setPaymentForms(list);
        if (list[0]?.id) {
          setFormData(prev => (prev.paymentFormId ? prev : { ...prev, paymentFormId: list[0].id }));
        }
      } catch (error) {
        console.error('Error loading payment forms:', error);
        setMessage({ type: 'error', text: 'Error al cargar las formas de pago' });
      } finally {
        setIsLoadingForms(false);
      }
    };

    const loadUsers = async () => {
      try {
        setIsLoadingUsers(true);
        // Load up to 100 users for the dropdown
        const result = await UserService.getAllUsers(auth.user?.access_token, 100);
        setUsers(result.users || result.data || []);
      } catch (error) {
        console.error('Error loading users:', error);
        // Non-critical, just log it
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (auth.user?.access_token) {
      loadForms();
      loadUsers();
    }
  }, [auth.user?.access_token]);

  useEffect(() => {
    const loadPatientData = async () => {
      if (formData.patientId && !selectedPatient && auth.user?.access_token) {
        try {
          const patientData = await PatientService.getPatientById(formData.patientId, auth.user.access_token);
          setSelectedPatient(patientData);
        } catch (error) {
          console.error('Error loading patient data:', error);
        }
      }
    };

    loadPatientData();
  }, [formData.patientId, selectedPatient, auth.user?.access_token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'date') setDateTouched(true);
  };

  const validate = () => {
    const errs = {};
    if (!formData.patientId) errs.patientId = 'Debe seleccionar un paciente';
    if (!formData.paymentFormId) errs.paymentFormId = 'Seleccione una forma de pago';
    const amount = Number(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) errs.amount = 'Ingrese un monto válido';
    if (!formData.date) errs.date = 'La fecha es requerida';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        patientId: formData.patientId,
        paymentFormId: formData.paymentFormId,
        amount: Number(formData.amount),
        date: formData.date,
        concept: formData.concept.trim(),
        note: formData.note.trim(),
        allocationUserId: formData.allocationUserId
      };
      const resp = await PaymentService.createPayment(payload, auth.user?.access_token);
      const created = resp;
      const pid = payload.patientId || created?.patientId;
      onSuccess && onSuccess(pid, created);
    } catch (error) {
      console.error('Error creating payment:', error);
      setMessage({ type: 'error', text: error.message || 'No se pudo registrar el pago' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setFormData(prev => ({ ...prev, patientId: patient?.id || '' }));
    if (errors.patientId) setErrors(prev => ({ ...prev, patientId: '' }));
  };

  return (
    <div className={`payment-form-container ${isModal ? 'modal' : ''}`}>
      {!isModal && (
        <div className="payment-form-header">
          <h1>Registrar Pago</h1>
          <p>Complete los detalles para registrar el pago del paciente</p>
        </div>
      )}

      <form className="payment-form" onSubmit={handleSubmit}>
        {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="patientId">Paciente <span className="required">*</span></label>
            <PatientAutocomplete
              selectedPatient={selectedPatient}
              onPatientSelect={handlePatientSelect}
              placeholder="Seleccione un paciente"
              searchPlaceholder="Buscar paciente por nombre..."
              disabled={isLoading}
              className={errors.patientId ? 'error' : ''}
              showPatientId={true}
              showBirthDate={true}
              minSearchLength={2}
              maxResults={50}
            />
            {errors.patientId && <span className="error-text">{errors.patientId}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="paymentFormId">Forma de Pago <span className="required">*</span></label>
            <select id="paymentFormId" name="paymentFormId" value={formData.paymentFormId} onChange={handleInputChange} className={errors.paymentFormId ? 'error' : ''} disabled={isLoadingForms}>
              <option value="">{isLoadingForms ? 'Cargando formas de pago...' : 'Seleccione'}</option>
              {paymentForms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            {errors.paymentFormId && <span className="error-text">{errors.paymentFormId}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="allocationUserId">Asignar a / Realizado por</label>
            <select 
              id="allocationUserId" 
              name="allocationUserId" 
              value={formData.allocationUserId} 
              onChange={handleInputChange} 
              disabled={isLoadingUsers}
            >
              <option value="GENERAL_SALES">Venta General</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name || u.firstName || u.username || u.email} {u.role ? `(${u.role})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="amount">Monto (MXN) <span className="required">*</span></label>
            <input type="text" inputMode="decimal" id="amount" name="amount" value={formData.amount} onChange={handleInputChange} className={errors.amount ? 'error' : ''} />
            {errors.amount && <span className="error-text">{errors.amount}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="date">Fecha efectiva <span className="required">*</span></label>
            <input type="date" id="date" name="date" value={formData.date} onChange={handleInputChange} className={errors.date ? 'error' : ''} />
            {errors.date && <span className="error-text">{errors.date}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="concept">Concepto</label>
            <input
              type="text"
              id="concept"
              name="concept"
              value={formData.concept}
              onChange={handleInputChange}
              placeholder="Opcional"
              maxLength={200}
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label htmlFor="note">Nota</label>
          <textarea id="note" name="note" rows={4} value={formData.note} onChange={handleInputChange} placeholder="Opcional" />
        </div>

        <div className="form-actions">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>Guardar</Button>
        </div>
      </form>
    </div>
  );
};

export default PaymentCreateForm;
